import debounce from 'lodash/debounce'
import { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ErrorVariant, Organisation } from '@percent/domain/verifications'
import { useProgress } from '@percent/product-nomination-form/hooks'
import { Card, OrganisationSelect } from '@percent/product-nomination-form/ui'
import { ErrorCard } from '@percent/product-nomination-form/ui/ErrorCard/ErrorCard'
import { useValidationStepData } from '../../validationStepContext/useValidationStepData/useValidationStepData'

import { HostedFormStep } from '../../HostedForm.types'
import { SelectOrganisationProps } from './SelectOrganisation.types'
import { SubmitNominationFormPayload } from '@percent/product-nomination-form/api/nominationForm/nominationForm.types'

export const SelectOrganisation = ({
  data,
  setSearchQuery,
  isError,
  country,
  isLoading,
}: SelectOrganisationProps) => {
  const [organisationQuery, setOrganisationQuery] = useState('')
  const { setOrganisation, handleNext, handleBack } = useValidationStepData()
  const { setValue } = useFormContext<SubmitNominationFormPayload>()
  const { t } = useTranslation()
  const { progress } = useProgress()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
    }, 500),
    []
  )

  const handleSelectOrganisation = (organisation?: Organisation) => {
    if (!organisation) return

    setOrganisation(organisation)
    setValue('organisationId', organisation.id)

    handleNext(HostedFormStep.ORGANISATION_RESULT)
  }

  if (isError) {
    return (
      <ErrorCard
        title={t('hostedValidation.errorMessage.somethingWentWrong')}
        subtitle={t('hostedValidation.errorMessage.reloadPage')}
        variant={ErrorVariant.INTERNAL}
      />
    )
  }

  const handleOrganisationQueryChange = (value: string) => {
    setOrganisationQuery(value)
    handleDebouncedSearch(value)
    setOrganisation(undefined)
  }

  const handleAddNonProfit = () => {
    handleNext(HostedFormStep.REGISTER_ORGANISATION)
  }

  return (
    <Card
      onBack={handleBack}
      title={t('hostedValidation.title.fullLegalName')}
      description={t('hostedValidation.title.fullLegalNameSub')}
      progress={progress}
    >
      <OrganisationSelect
        data={data?.data}
        isLoading={isLoading}
        name="organisation"
        setOrganisationValue={handleSelectOrganisation}
        setQueryValue={handleOrganisationQueryChange}
        countryCode={country}
        query={organisationQuery}
        placeholder={t('hostedValidation.placeholder.nonprofitNameOrRegId')}
        onAddNonProfit={handleAddNonProfit}
      />
    </Card>
  )
}
